/**
 * Quiz Result Personalization Data
 *
 * Structure: 3 profiles × 6 personas × 3 durations = 18 content variations + 3 duration overlays
 *
 * - Profiles: Medical/educational content specific to metabolic type
 * - Personas: Marketing angles and emotional benefits
 * - Durations: Timeline and justification based on score intensity
 */

(function() {
  'use strict';

  window.quizResultData = {

    // ========================================
    // PROFILES - Medical/Educational Content
    // ========================================

    profiles: {
      hepatic: {
        name: 'Hépatique',
        icon: '🫘',
        color: '#108474',

        hero: {
          titleTemplate: 'Votre {GOAL} Bloqué par Votre Foie',
          subtitle: 'Votre foie surchargé ne filtre plus correctement les toxines. Ce n\'est pas une question de graisse, mais de détoxification hépatique.',
          visual: 'hepatic-illustration.png'
        },

        whyYou: {
          dominantIssue: 'Votre FOIE surchargé qui bloque tout',
          failedMethods: [
            'Ce n\'est PAS un problème de calories',
            'Ce n\'est PAS un manque de volonté',
            'Ce n\'est PAS juste de la graisse'
          ],
          explanation: 'Votre foie, en surcharge toxique, ne parvient plus à métaboliser les graisses correctement. Il stocke au lieu d\'éliminer.'
        },

        timeline: [
          { week: '1-2', text: 'Digestion des graisses améliorée', icon: '🥗' },
          { week: '3-4', text: 'Sommeil réparateur sans réveils nocturnes', icon: '😴' },
          { week: '5-6', text: 'Énergie stable du matin au soir', icon: '⚡' },
          { week: '7-8', text: 'Teint lumineux et peau éclatante', icon: '✨' }
        ],

        mechanism: {
          phase1: 'Activation de la détoxification hépatique (Phase 1 et 2)',
          phase2: 'Régénération cellulaire et drainage biliaire',
          phase3: 'Consolidation et protection hépatique durable'
        }
      },

      lymphatic: {
        name: 'Lymphatique',
        icon: '💧',
        color: '#4A90E2',

        hero: {
          titleTemplate: 'Votre {GOAL} Bloqué par la Rétention d\'Eau',
          subtitle: 'Votre système lymphatique congestionné ne draine plus efficacement. L\'eau et les toxines stagnent dans vos tissus.',
          visual: 'lymphatic-illustration.png'
        },

        whyYou: {
          dominantIssue: 'Votre LYMPHE congestionnée qui stagne',
          failedMethods: [
            'Ce n\'est PAS de la graisse',
            'Ce n\'est PAS un manque d\'exercice',
            'Ce n\'est PAS juste de la cellulite'
          ],
          explanation: 'Votre système lymphatique, ralenti, accumule eau et déchets métaboliques. Cela crée gonflements, cellulite et sensation de jambes lourdes.'
        },

        timeline: [
          { week: '1-2', text: 'Dégonflement visible des chevilles et pieds', icon: '🦶' },
          { week: '3-4', text: 'Circulation améliorée, jambes plus légères', icon: '🦵' },
          { week: '5-6', text: 'Réduction de la cellulite aqueuse', icon: '🍊' },
          { week: '7-8', text: 'Silhouette affinée et tonique', icon: '👗' }
        ],

        mechanism: {
          phase1: 'Activation du drainage lymphatique profond',
          phase2: 'Élimination de la rétention d\'eau et des toxines',
          phase3: 'Consolidation de la circulation et prévention'
        }
      },

      lipid: {
        name: 'Lipidique',
        icon: '🔥',
        color: '#E67E22',

        hero: {
          titleTemplate: 'Votre {GOAL} Bloqué par Votre Métabolisme',
          subtitle: 'Votre métabolisme lipidique et glycémique est déréglé. Votre corps stocke au lieu de brûler les graisses.',
          visual: 'lipid-illustration.png'
        },

        whyYou: {
          dominantIssue: 'Votre MÉTABOLISME bloqué qui stocke tout',
          failedMethods: [
            'Ce n\'est PAS un manque de motivation',
            'Ce n\'est PAS juste des fringales',
            'Ce n\'est PAS une question de calories'
          ],
          explanation: 'Votre résistance à l\'insuline empêche la combustion des graisses. Votre glycémie instable génère fringales et stockage abdominal.'
        },

        timeline: [
          { week: '1-2', text: 'Fringales réduites, glycémie stabilisée', icon: '📊' },
          { week: '3-4', text: 'Énergie constante sans coups de fatigue', icon: '⚡' },
          { week: '5-6', text: 'Fonte de la graisse abdominale', icon: '🫄' },
          { week: '7-8', text: 'Poids stable et métabolisme réactivé', icon: '🔥' }
        ],

        mechanism: {
          phase1: 'Rééquilibrage glycémique et sensibilité à l\'insuline',
          phase2: 'Activation de la lipolyse (combustion des graisses)',
          phase3: 'Stabilisation métabolique et prévention du rebond'
        }
      }
    },

    // ========================================
    // PERSONAS - Marketing Angles & Emotional Benefits
    // ========================================

    personas: {
      // FEMMES
      emma: {
        name: 'Emma',
        goal: 'Ventre Plat',
        gender: 'female',

        benefits: [
          'Dégonflez rapidement et visiblement',
          'Rentrez dans vos vêtements favoris',
          'Silhouette affinée et harmonieuse',
          'Confiance retrouvée en quelques semaines'
        ],

        cta: {
          primary: 'Retrouvez Votre Ventre Plat Naturellement',
          secondary: 'Commencer Ma Cure Personnalisée'
        },

        testimonial: {
          name: 'Sophie, 34 ans',
          text: 'En 6 semaines, -4 cm de tour de taille ! Mon ventre était gonflé en permanence, même le matin à jeun. Maintenant je me sens légère et mes vêtements tombent parfaitement.',
          image: 'testimonial-sophie.jpg',
          result: '-4 cm de tour de taille'
        },

        painPoints: [
          'Ventre gonflé même à jeun',
          'Impossible de rentrer dans vos pantalons',
          'Complexée par votre silhouette'
        ]
      },

      clara: {
        name: 'Clara',
        goal: 'Légèreté',
        gender: 'female',

        benefits: [
          'Sentez-vous légère après les repas',
          'Éliminez la sensation de lourdeur',
          'Retrouvez votre vitalité naturelle',
          'Digestion fluide et confortable'
        ],

        cta: {
          primary: 'Retrouvez Votre Légèreté Naturelle',
          secondary: 'Commencer Ma Détox Personnalisée'
        },

        testimonial: {
          name: 'Marie, 41 ans',
          text: 'Je me sentais lourde en permanence, surtout après les repas. En 3 semaines, digestion transformée ! Je me sens enfin légère et énergique.',
          image: 'testimonial-marie.jpg',
          result: 'Digestion transformée en 3 semaines'
        },

        painPoints: [
          'Sensation de lourdeur constante',
          'Fatigue après les repas',
          'Manque d\'énergie et de vitalité'
        ]
      },

      julie: {
        name: 'Julie',
        goal: 'Éclat',
        gender: 'female',

        benefits: [
          'Peau lumineuse et éclatante',
          'Silhouette harmonieuse et tonique',
          'Teint unifié sans imperfections',
          'Rayonnez de l\'intérieur'
        ],

        cta: {
          primary: 'Révélez Votre Éclat Naturel',
          secondary: 'Commencer Ma Cure Beauté'
        },

        testimonial: {
          name: 'Léa, 38 ans',
          text: 'Ma peau était terne et mon teint brouillé. En 2 mois, transformation visible ! Teint lumineux, peau lisse, et -3 kg sans effort.',
          image: 'testimonial-lea.jpg',
          result: 'Teint lumineux + -3 kg'
        },

        painPoints: [
          'Peau terne et fatiguée',
          'Teint brouillé ou grisâtre',
          'Manque de confiance en votre apparence'
        ]
      },

      // HOMMES
      thomas: {
        name: 'Thomas',
        goal: 'Ventre Plat',
        gender: 'male',

        benefits: [
          'Perdez votre bedaine rapidement',
          'Retrouvez un ventre plat et ferme',
          'Silhouette plus athlétique',
          'Confiance et performance retrouvées'
        ],

        cta: {
          primary: 'Perdez Votre Bedaine Naturellement',
          secondary: 'Commencer Mon Protocole'
        },

        testimonial: {
          name: 'David, 45 ans',
          text: 'Bedaine tenace malgré le sport. En 2 mois de cure, -6 cm de tour de taille ! Mon ventre est enfin plat, sans régime restrictif.',
          image: 'testimonial-david.jpg',
          result: '-6 cm de tour de taille'
        },

        painPoints: [
          'Bedaine qui ne part pas malgré les efforts',
          'Graisse abdominale tenace',
          'Complexé par votre silhouette'
        ]
      },

      pierre: {
        name: 'Pierre',
        goal: 'Performance',
        gender: 'male',

        benefits: [
          'Énergie constante toute la journée',
          'Performance physique optimisée',
          'Concentration et clarté mentale',
          'Récupération accélérée'
        ],

        cta: {
          primary: 'Optimisez Votre Performance Naturellement',
          secondary: 'Commencer Mon Protocole Performance'
        },

        testimonial: {
          name: 'Julien, 39 ans',
          text: 'Fatigué en permanence, coups de barre l\'après-midi. Depuis la cure, énergie stable, meilleure concentration au travail, et performances sportives au top.',
          image: 'testimonial-julien.jpg',
          result: 'Énergie et performance retrouvées'
        },

        painPoints: [
          'Fatigue chronique et coups de barre',
          'Manque d\'énergie pour le sport',
          'Concentration difficile au travail'
        ]
      },

      marc: {
        name: 'Marc',
        goal: 'Digestion',
        gender: 'male',

        benefits: [
          'Digestion fluide et confortable',
          'Fini les ballonnements et lourdeurs',
          'Sommeil réparateur',
          'Bien-être digestif retrouvé'
        ],

        cta: {
          primary: 'Soulagez Votre Digestion Naturellement',
          secondary: 'Commencer Ma Cure Digestive'
        },

        testimonial: {
          name: 'Patrick, 52 ans',
          text: 'Digestion difficile depuis des années, surtout les graisses. En 1 mois, transformation ! Je digère tout sans problème, sommeil amélioré.',
          image: 'testimonial-patrick.jpg',
          result: 'Digestion transformée en 1 mois'
        },

        painPoints: [
          'Digestion difficile des graisses',
          'Ballonnements après les repas',
          'Réveils nocturnes entre 1h et 3h'
        ]
      }
    },

    // ========================================
    // DURATIONS - Timeline & Justification
    // ========================================

    durations: {
      discovery: {
        months: 1,
        label: 'Cure Découverte',
        level: 'discovery',
        scoreRange: '0-4',

        description: 'Votre score indique un déséquilibre léger qui nécessite une intervention ciblée.',

        timeline: {
          title: 'Votre Protocole 1 Mois',
          description: 'Une cure ciblée pour corriger un déséquilibre léger',
          phases: [
            {
              period: 'Semaines 1-2',
              title: 'Activation',
              description: 'Démarrage du processus de détoxification',
              results: 'Premiers signes d\'amélioration visible'
            },
            {
              period: 'Semaines 3-4',
              title: 'Optimisation',
              description: 'Intensification des résultats',
              results: 'Transformation notable et mesurable'
            }
          ]
        },

        justification: 'Une cure d\'1 mois suffit pour corriger un déséquilibre léger et obtenir des résultats visibles. Idéal pour un premier protocole de détoxification.',

        packs: {
          recommended: 1,
          available: [1, 2, 3]
        }
      },

      standard: {
        months: 2,
        label: 'Cure Standard',
        level: 'standard',
        scoreRange: '5-7',

        description: 'Votre score indique un déséquilibre modéré nécessitant une cure complète de 2 mois.',

        timeline: {
          title: 'Votre Protocole 2 Mois',
          description: 'Un programme complet pour un rééquilibrage en profondeur',
          phases: [
            {
              period: 'Mois 1',
              title: 'Activation & Détoxification',
              description: 'Démarrage du processus de rééquilibrage',
              results: 'Premiers résultats visibles dès 2-3 semaines'
            },
            {
              period: 'Mois 2',
              title: 'Consolidation & Transformation',
              description: 'Optimisation et stabilisation des résultats',
              results: 'Transformation durable et mesurable'
            }
          ]
        },

        justification: 'Votre déséquilibre modéré nécessite 2 mois complets pour permettre au corps de se régénérer en profondeur. C\'est le protocole optimal pour des résultats durables.',

        warning: 'Une cure trop courte (1 mois) ne suffirait pas à corriger complètement votre profil.',

        packs: {
          recommended: 2,
          available: [1, 2, 3, 4]
        }
      },

      intensive: {
        months: 3,
        label: 'Cure Intensive',
        level: 'intensive',
        scoreRange: '8-10',

        description: 'Votre score indique un déséquilibre important nécessitant une intervention intensive de 3 mois.',

        timeline: {
          title: 'Votre Protocole 3 Mois',
          description: 'Un protocole intensif pour une transformation complète',
          phases: [
            {
              period: 'Mois 1',
              title: 'Détoxification Profonde',
              description: 'Activation des processus d\'élimination',
              results: 'Amélioration progressive des symptômes'
            },
            {
              period: 'Mois 2',
              title: 'Rééquilibrage',
              description: 'Régénération cellulaire et métabolique',
              results: 'Transformation visible et mesurable'
            },
            {
              period: 'Mois 3',
              title: 'Consolidation Durable',
              description: 'Stabilisation et prévention du rebond',
              results: 'Résultats durables et profonds'
            }
          ]
        },

        justification: 'Votre déséquilibre important nécessite un protocole intensif de 3 mois pour permettre une régénération complète et durable. C\'est un investissement dans votre santé à long terme.',

        warning: 'Une cure de 1 ou 2 mois serait insuffisante pour corriger un profil aussi marqué. Vous risqueriez de ne pas obtenir les résultats espérés.',

        packs: {
          recommended: 3,
          available: [2, 3, 4]
        }
      }
    },

    // ========================================
    // HELPER FUNCTIONS
    // ========================================

    /**
     * Get personalized content for a given profile, persona, and duration
     * @param {string} profile - hepatic, lymphatic, lipid
     * @param {string} persona - emma, clara, julie, thomas, pierre, marc
     * @param {object} duration - Duration object from quiz result
     * @returns {object} Compiled personalized content
     */
    getPersonalizedContent: function(profile, persona, duration) {
      const profileData = this.profiles[profile] || this.profiles.hepatic;
      const personaData = this.personas[persona] || this.personas.emma;
      const durationData = this.durations[duration.level] || this.durations.standard;

      // Merge profile + persona + duration
      return {
        profile: profileData,
        persona: personaData,
        duration: durationData,

        // Compiled hero section
        hero: {
          title: profileData.hero.titleTemplate.replace('{GOAL}', personaData.goal),
          subtitle: profileData.hero.subtitle,
          visual: profileData.hero.visual,
          cta: personaData.cta.primary,
          color: profileData.color,
          icon: profileData.icon
        },

        // Compiled results section
        results: {
          timeline: profileData.timeline,
          benefits: personaData.benefits,
          testimonial: personaData.testimonial
        },

        // Duration specific
        durationInfo: {
          months: durationData.months,
          label: durationData.label,
          timeline: durationData.timeline,
          justification: durationData.justification,
          warning: durationData.warning || null,
          packs: durationData.packs
        }
      };
    }
  };

  // Expose globally
  console.log('✅ Quiz Result Personalization Data loaded');

  // ========================================
  // INITIALIZE FROM URL HASH OR SESSIONSTORE
  // ========================================

  /**
   * Load quiz result data from URL hash (base64) or fallback to sessionStorage
   */
  function loadQuizResultFromURL() {
    try {
      // Try to load from URL hash first (shareable link)
      if (window.location.hash) {
        const base64Data = window.location.hash.substring(1); // Remove '#'
        const jsonString = atob(base64Data);
        const resultData = JSON.parse(jsonString);

        console.log('✅ Quiz result loaded from URL hash:', resultData);

        // Store in sessionStorage for compatibility with existing sections
        sessionStorage.setItem('quizState', JSON.stringify({
          result: {
            primary: resultData.profile,
            persona: resultData.persona,
            scores: resultData.scores,
            duration: resultData.duration
          }
        }));

        return resultData;
      }

      // Fallback to sessionStorage (legacy behavior)
      const quizState = sessionStorage.getItem('quizState');
      if (quizState) {
        const state = JSON.parse(quizState);
        if (state.result) {
          console.log('✅ Quiz result loaded from sessionStorage (legacy)');
          return {
            profile: state.result.primary,
            persona: state.result.persona,
            scores: state.result.scores,
            duration: state.result.duration
          };
        }
      }

      console.warn('⚠️ No quiz result found in URL or sessionStorage');
      return null;

    } catch (error) {
      console.error('❌ Error loading quiz result:', error);
      return null;
    }
  }

  // Load result data on page load
  const resultData = loadQuizResultFromURL();
  if (resultData) {
    // Store for easy access by sections
    window.currentQuizResult = resultData;
    console.log('✅ Current quiz result available globally:', window.currentQuizResult);
  }

})();
